import { useEffect, useRef } from 'react';
import clsx from 'clsx';

interface WaveformProps {
  audioData?: Float32Array;
  isActive: boolean;
  className?: string;
  barCount?: number;
  color?: 'primary' | 'secondary' | 'accent';
}

export function Waveform({
  audioData,
  isActive,
  className,
  barCount = 32,
  color = 'primary',
}: WaveformProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const colorMap = {
    primary: '#6366f1',
    secondary: '#8b5cf6',
    accent: '#06b6d4',
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const draw = () => {
      const { width, height } = canvas;
      ctx.clearRect(0, 0, width, height);

      const barWidth = width / barCount;
      const barGap = 2;

      for (let i = 0; i < barCount; i++) {
        let barHeight: number;

        if (isActive && audioData && audioData.length > 0) {
          // Use actual audio data
          const dataIndex = Math.floor((i / barCount) * audioData.length);
          barHeight = Math.abs(audioData[dataIndex] || 0) * height * 2;
        } else if (isActive) {
          // Simulate waveform when no data
          barHeight = (Math.sin(Date.now() / 200 + i * 0.5) + 1) * height * 0.3;
        } else {
          // Idle state
          barHeight = 4;
        }

        barHeight = Math.max(4, Math.min(barHeight, height - 4));

        const x = i * barWidth + barGap / 2;
        const y = (height - barHeight) / 2;

        ctx.fillStyle = colorMap[color];
        ctx.beginPath();
        ctx.roundRect(x, y, barWidth - barGap, barHeight, 2);
        ctx.fill();
      }
    };

    let animationId: number;

    const animate = () => {
      draw();
      animationId = requestAnimationFrame(animate);
    };

    if (isActive) {
      animate();
    } else {
      draw();
    }

    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, [audioData, isActive, barCount, color]);

  return (
    <canvas
      ref={canvasRef}
      width={300}
      height={60}
      className={clsx('w-full h-16', className)}
    />
  );
}
